import { useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { guardarGiro } from "@/modules/analisis_financiero/analisisFinancieroApi";
import {
  claveActividad,
  etiquetaOrigen,
  giroDesdeApi,
} from "@/modules/analisis_financiero/giroConstanciaUi";

const MSG_CONSTANCIA = "Origen: constancia. Puede elegir otra actividad o modificar el giro.";
const MSG_MANUAL = "Origen: manual. Puede modificar el giro.";
const MSG_SIN_COINCIDENCIA = (
  "La constancia no coincide con los giros de la tabla de referencias. "
  + "Seleccione o capture el giro. Puede modificarlo."
);

export default function GiroConstanciaPanel({
  clientId,
  giro,
  setGiro,
  puedeEscribir,
  onGuardado,
}) {
  const [guardando, setGuardando] = useState(false);
  const actividades = giro?.actividades || [];
  const giros = giro?.giros_referencia || [];
  const marcada = actividades.find((act) => (
    act.descripcion === giro?.actividad_descripcion
    && (giro?.actividad_orden == null || act.orden === giro?.actividad_orden)
  ));
  const claveActual = marcada ? claveActividad(marcada) : "";
  const bloqueado = !puedeEscribir;

  const elegirActividad = (clave) => {
    const act = actividades.find((item) => claveActividad(item) === clave);
    if (!act) return;
    if (act.coincide) {
      setGiro({
        giro_scian: act.giro_scian,
        giro_descripcion: act.giro_descripcion,
        origen_giro: "constancia",
        guardado: false,
        actividad_orden: act.orden ?? null,
        actividad_descripcion: act.descripcion,
        actividad_porcentaje: act.porcentaje ?? null,
        mensaje: MSG_CONSTANCIA,
      });
      return;
    }
    setGiro({
      giro_scian: "",
      giro_descripcion: "",
      origen_giro: "",
      guardado: false,
      actividad_orden: act.orden ?? null,
      actividad_descripcion: act.descripcion,
      actividad_porcentaje: act.porcentaje ?? null,
      mensaje: MSG_SIN_COINCIDENCIA,
    });
  };

  const elegirTabla = (scian) => {
    const row = giros.find((item) => item.giro_scian === scian);
    const act = actividades.find((item) => claveActividad(item) === claveActual);
    const desdeConstancia = Boolean(act && act.coincide && act.giro_scian === scian);
    setGiro({
      giro_scian: scian,
      giro_descripcion: row?.giro_descripcion || giro?.giro_descripcion || "",
      origen_giro: desdeConstancia ? "constancia" : "manual",
      guardado: false,
      mensaje: desdeConstancia ? MSG_CONSTANCIA : MSG_MANUAL,
    });
  };

  const editar = (campo, valor) => {
    const parcial = {
      [campo]: valor,
      origen_giro: "manual",
      guardado: false,
      mensaje: MSG_MANUAL,
    };
    if (campo === "giro_scian") {
      const row = giros.find((item) => item.giro_scian === String(valor || "").trim());
      if (row) parcial.giro_descripcion = row.giro_descripcion;
    }
    setGiro(parcial);
  };

  const guardar = async () => {
    if (bloqueado || !clientId) return;
    setGuardando(true);
    try {
      const { data } = await guardarGiro(clientId, giro);
      const siguiente = giroDesdeApi(data);
      if (onGuardado) onGuardado(siguiente);
      else setGiro(siguiente);
      toast.success("Se guardó el giro del contribuyente.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo guardar el giro.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className="rounded-sm border border-slate-200 p-3 space-y-3" data-testid="panel-giro-constancia">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[#0F2D5C]">Giro del contribuyente</h3>
        <span
          className="rounded-sm border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700"
          data-testid="origen-giro"
        >
          {etiquetaOrigen(giro?.origen_giro)}
        </span>
      </div>
      <p className="text-xs text-slate-600" data-testid="mensaje-giro">
        {giro?.mensaje || "Seleccione el giro en la tabla de referencias o captúrelo. Puede modificarlo."}
      </p>
      {!clientId ? (
        <p className="text-sm text-slate-500">Seleccione una empresa para ver el giro.</p>
      ) : (
        <>
          {actividades.length ? (
            <ul className="space-y-1 text-xs text-slate-700" data-testid="lista-actividades-constancia">
              {actividades.map((act) => {
                const sugerida = giro?.sugerencia
                  && act.descripcion === giro.sugerencia.descripcion
                  && act.orden === giro.sugerencia.orden;
                return (
                  <li key={claveActividad(act)}>
                    {act.porcentaje != null ? `${act.porcentaje}%` : "sin porcentaje"}
                    {" · "}
                    {act.descripcion}
                    {act.coincide ? ` · ${act.giro_scian}` : " · sin coincidencia en la tabla"}
                    {sugerida ? " · preseleccionada por mayor porcentaje" : ""}
                  </li>
                );
              })}
            </ul>
          ) : null}
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-xs">Actividad de la constancia
              <select
                className="mt-1 w-full rounded-sm border px-2 py-1.5 text-sm"
                value={claveActual}
                disabled={bloqueado || !actividades.length}
                onChange={(e) => elegirActividad(e.target.value)}
                data-testid="select-actividad-constancia"
              >
                <option value="">
                  {actividades.length ? "Elija otra actividad" : "Sin actividades en la constancia"}
                </option>
                {actividades.map((act) => (
                  <option key={claveActividad(act)} value={claveActividad(act)}>
                    {act.porcentaje != null ? `${act.porcentaje}% · ` : ""}
                    {act.descripcion}
                    {act.coincide ? ` → ${act.giro_scian}` : " · sin coincidencia"}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs">Giro de la tabla de referencias
              <select
                className="mt-1 w-full rounded-sm border px-2 py-1.5 text-sm"
                value={giros.some((g) => g.giro_scian === giro?.giro_scian) ? giro.giro_scian : ""}
                disabled={bloqueado}
                onChange={(e) => elegirTabla(e.target.value)}
                data-testid="select-giro-tabla"
              >
                <option value="">Seleccione o capture el giro</option>
                {giros.map((g) => (
                  <option key={g.giro_scian} value={g.giro_scian}>
                    {g.giro_scian} · {g.giro_descripcion}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs">Giro SCIAN
              <input
                className="mt-1 block w-full rounded-sm border px-2 py-1.5 text-sm"
                value={giro?.giro_scian || ""}
                disabled={bloqueado}
                onChange={(e) => editar("giro_scian", e.target.value)}
                placeholder="541211"
                data-testid="giro-scian"
              />
            </label>
            <label className="text-xs">Descripción del giro
              <input
                className="mt-1 block w-full rounded-sm border px-2 py-1.5 text-sm"
                value={giro?.giro_descripcion || ""}
                disabled={bloqueado}
                onChange={(e) => editar("giro_descripcion", e.target.value)}
                data-testid="giro-descripcion"
              />
            </label>
          </div>
          <button
            type="button"
            className="rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            disabled={bloqueado || guardando}
            onClick={guardar}
            data-testid="guardar-giro"
          >
            {guardando ? "Guardando…" : "Guardar giro"}
          </button>
        </>
      )}
    </section>
  );
}
